import { defineStore } from "pinia";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import { ref } from "vue";
import HierarchyNamingStyle = App.Enums.HierarchyNamingStyle;

export type HierarchyKey = {
	id: number;
	name_style: HierarchyNamingStyle;
};

export const useHierarchyStore = defineStore("hierarchyStore", () => {
	const staticHierarchyNodes = ref<Record<string, QuickFilterNodeDTO>>({});

	const toRecordKey = (key: HierarchyKey) => JSON.stringify(key);

	const hasHierarchyNodes = (hierarchyKey: HierarchyKey) =>
		!!staticHierarchyNodes.value[toRecordKey(hierarchyKey)];

	const setStaticHierarchyNodes = (
		hierarchyKey: HierarchyKey,
		branches: QuickFilterNodeDTO
	) => {
		staticHierarchyNodes.value[toRecordKey(hierarchyKey)] = branches;
	};

	const getHierarchyNodes = (hierarchyKey: HierarchyKey) =>
		staticHierarchyNodes.value[toRecordKey(hierarchyKey)];

	const findNodeByLabel = (
		node: QuickFilterNodeDTO,
		label: string
	): QuickFilterNodeDTO | null => {
		if (node.label === label) return node;
		if (node.children) {
			for (const child of node.children) {
				const result = findNodeByLabel(child, label);
				if (result) return result;
			}
		}
		return null;
	};

	const findParentByLabelAndLevel = (
		node: QuickFilterNodeDTO,
		targetLabel: string,
		targetLevel: number
	): QuickFilterNodeDTO | undefined => {
		if (node.children) {
			for (const child of node.children) {
				if (child.label === targetLabel && child.level === targetLevel) {
					return node;
				}
				const result = findParentByLabelAndLevel(
					child,
					targetLabel,
					targetLevel
				);
				if (result !== undefined) return result;
			}
		}
		return undefined;
	};

	const getOrLoadHierarchy = async (
		hierarchyKey: HierarchyKey,
		load: () => Promise<QuickFilterNodeDTO | null>
	): Promise<QuickFilterNodeDTO | null> => {
		if (hasHierarchyNodes(hierarchyKey)) {
			return getHierarchyNodes(hierarchyKey);
		}
		const node = await load();
		if (node) {
			setStaticHierarchyNodes(hierarchyKey, node);
		}
		return node;
	};

	return {
		staticHierarchyNodes,
		hasHierarchyNodes,
		setStaticHierarchyNodes,
		getHierarchyNodes,
		getOrLoadHierarchy,
		findNodeByLabel,
		findParentByLabelAndLevel,
	};
});
